import { BOARD_SIZE, cellIndex, inBounds } from './board';
import type { Direction, StoneColor, WinCheckResult } from './types';

/**
 * 3次元の5目判定で検査する13方向。
 * 逆向きの同一直線を重複検査しないよう、
 * 最初に変化する成分が +1 になるように正規化して定義する。
 *
 * - 軸方向 3: X, Y, Z
 * - 平面斜め 6: XY, XZ, YZ 平面
 * - 空間対角 4: 3次元対角線
 */
export const DIRECTIONS: readonly Direction[] = [
  // 軸
  [1, 0, 0],
  [0, 1, 0],
  [0, 0, 1],
  // XY 平面
  [1, 1, 0],
  [1, -1, 0],
  // XZ 平面
  [1, 0, 1],
  [1, 0, -1],
  // YZ 平面
  [0, 1, 1],
  [0, 1, -1],
  // 空間対角
  [1, 1, 1],
  [1, 1, -1],
  [1, -1, 1],
  [1, -1, -1],
];

/**
 * 5目（5つ以上の一直線）を判定する。
 *
 * @param colors 長さ 1000 の配列。index = x + y*10 + z*100 で石の色を保持し、
 *               石がないマスは null。
 * @returns 黒の5目が成立したか / 白の5目が成立したか
 */
export function checkWin(colors: ReadonlyArray<StoneColor | null>): WinCheckResult {
  const result: WinCheckResult = { black: false, white: false };

  for (let z = 0; z < BOARD_SIZE; z++) {
    for (let y = 0; y < BOARD_SIZE; y++) {
      for (let x = 0; x < BOARD_SIZE; x++) {
        const color = colors[cellIndex(x, y, z)];
        if (color === null) continue;
        // 既にその色の5目が見つからなければ、以降の探索を省略
        if (color === 'black' && result.black) continue;
        if (color === 'white' && result.white) continue;

        for (const [dx, dy, dz] of DIRECTIONS) {
          // このマスが「連続列の先頭」である場合のみ検査する。
          // 反対方向の隣接マスが同じ色なら、その列は先頭側で数えられている。
          const px = x - dx;
          const py = y - dy;
          const pz = z - dz;
          if (inBounds(px, py, pz) && colors[cellIndex(px, py, pz)] === color) {
            continue;
          }

          let run = 0;
          let cx = x;
          let cy = y;
          let cz = z;
          while (inBounds(cx, cy, cz) && colors[cellIndex(cx, cy, cz)] === color) {
            run++;
            cx += dx;
            cy += dy;
            cz += dz;
          }

          if (run >= 5) {
            result[color] = true;
          }
        }
      }
    }
  }

  return result;
}

/**
 * 5目（5つ以上）の成立する直線のマス座標を1本探し出す。
 * 勝利ラインのハイライト表示用。
 *
 * @returns 成立した直線の5マス以上（先頭から順）の座標。見つからなければ null
 */
export function findWinLine(
  colors: ReadonlyArray<StoneColor | null>,
  color: StoneColor,
): Array<[number, number, number]> | null {
  for (let z = 0; z < BOARD_SIZE; z++) {
    for (let y = 0; y < BOARD_SIZE; y++) {
      for (let x = 0; x < BOARD_SIZE; x++) {
        if (colors[cellIndex(x, y, z)] !== color) continue;

        for (const [dx, dy, dz] of DIRECTIONS) {
          const px = x - dx;
          const py = y - dy;
          const pz = z - dz;
          if (inBounds(px, py, pz) && colors[cellIndex(px, py, pz)] === color) continue;

          const line: Array<[number, number, number]> = [];
          let cx = x;
          let cy = y;
          let cz = z;
          while (inBounds(cx, cy, cz) && colors[cellIndex(cx, cy, cz)] === color) {
            line.push([cx, cy, cz]);
            cx += dx;
            cy += dy;
            cz += dz;
          }
          if (line.length >= 5) return line;
        }
      }
    }
  }
  return null;
}
